// Per-part attachment picker — lets the user choose which drawings from the
// quote-level pool (quote.attachedDrawings) apply to a specific part on the
// quote. Modal renders a grid of ~140px thumbnails with a checkbox overlay
// on each; ticked = currently in part.attachedDrawings. Save commits the
// picked list back through the parent's onSave handler (which calls
// updatePart in QuoteDetailPage).
//
// Why a modal vs inline: the pool can be 10+ drawings and the Builder tab
// already has a tall part-row layout. A modal gives us screen real estate
// for thumbnails without crowding the row.
//
// Edge cases:
//   - Empty pool — modal shows an empty-state and dims the Save button
//     (nothing to pick).
//   - Current part.attachedDrawings has URLs not in the pool — those are
//     surfaced in a separate "Off-pool" section so the user can drop them
//     intentionally. (Happens if a drawing was attached before the quote-pool
//     feature shipped, or via direct-to-part upload from PartDetailPage.)

import { useEffect, useMemo, useState } from 'react';
import type { Part, Quote } from '@/shared/crmTypes';
import { PdfThumbnail } from './PdfThumbnail';

export interface PartAttachmentPickerModalProps {
  open: boolean;
  onClose: () => void;
  /** Quote provides the attachment pool (quote.attachedDrawings). */
  quote: Quote;
  /** Part provides the current selection (part.attachedDrawings). */
  part: Part;
  /** Save handler — receives the new list of URLs picked for this part. */
  onSave: (attachedDrawings: string[]) => void | Promise<void>;
}

function shortName(entry: string): string {
  try {
    const u = new URL(entry);
    const last = u.pathname.split('/').filter(Boolean).pop();
    if (last) {
      const decoded = decodeURIComponent(last);
      return decoded.length > 40 ? `${decoded.slice(0, 37)}…` : decoded;
    }
  } catch {
    /* not a URL — fall through */
  }
  const parts = entry.split(/[/\\]/);
  const tail = parts[parts.length - 1] || entry;
  return tail.length > 40 ? `${tail.slice(0, 37)}…` : tail;
}

function isPdfUrl(entry: string): boolean {
  if (!/^(https?:|data:|blob:)/i.test(entry)) return false;
  const base = entry.split(/[?#]/)[0];
  return /\.pdf$/i.test(base);
}

function isImageUrl(entry: string): boolean {
  if (!/^(https?:|data:|blob:)/i.test(entry)) return false;
  const base = entry.split(/[?#]/)[0];
  return /\.(png|jpe?g|gif|webp|svg)$/i.test(base);
}

export function PartAttachmentPickerModal({
  open,
  onClose,
  quote,
  part,
  onSave,
}: PartAttachmentPickerModalProps) {
  const pool = useMemo(
    () => (quote as Quote & { attachedDrawings?: string[] }).attachedDrawings ?? [],
    [quote],
  );
  const initialPicked = useMemo(() => part.attachedDrawings ?? [], [part.attachedDrawings]);

  // Local edit buffer — committed on Save. Synced from the part whenever the
  // modal opens.
  const [picked, setPicked] = useState<Set<string>>(new Set(initialPicked));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setPicked(new Set(initialPicked));
  }, [open, initialPicked]);

  // Escape closes — same contract as other modals on this page.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  // Split: pool entries (the offered set) vs off-pool entries (URLs already
  // on the part but not in the quote pool — preserved across save unless the
  // user explicitly unchecks them).
  const poolSet = new Set(pool);
  const offPool = Array.from(picked).filter((u) => !poolSet.has(u));

  function toggle(url: string) {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(url)) next.delete(url);
      else next.add(url);
      return next;
    });
  }

  function pickAll() {
    setPicked((prev) => {
      const next = new Set(prev);
      for (const u of pool) next.add(u);
      return next;
    });
  }

  function clearAll() {
    setPicked(new Set());
  }

  async function handleSave() {
    setSaving(true);
    try {
      await onSave(Array.from(picked));
      onClose();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-navy/50 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="part-attachment-picker-title"
    >
      <div
        className="bg-paper rounded shadow-lg w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ─── Header ─── */}
        <div className="px-5 py-3 border-b border-line flex items-center justify-between bg-navy text-paper shrink-0 gap-3">
          <h2
            id="part-attachment-picker-title"
            className="font-bold text-base truncate min-w-0 flex-1"
          >
            Pick drawings for {part.number}
          </h2>
          <div className="text-xs text-paper/70 font-mono shrink-0">
            {picked.size} of {pool.length} picked
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-paper/80 hover:text-paper text-lg leading-none shrink-0"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* ─── Grid ─── */}
        <div className="flex-1 overflow-y-auto p-4">
          {pool.length === 0 && offPool.length === 0 ? (
            <div className="text-sm text-text-mute italic text-center py-12">
              No drawings in the quote pool yet. Drop drawings on the
              <span className="text-text font-semibold"> Quote attachments </span>
              panel at the top of the Builder tab first.
            </div>
          ) : (
            <>
              {pool.length > 0 && (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-[11px] uppercase tracking-wide text-text-mute font-semibold">
                      Pool ({pool.length})
                    </div>
                    <div className="flex items-center gap-2 text-[11px]">
                      <button
                        type="button"
                        onClick={pickAll}
                        className="text-text-mute hover:text-orange"
                      >
                        Select all
                      </button>
                      <span className="text-text-mute opacity-50">·</span>
                      <button
                        type="button"
                        onClick={clearAll}
                        className="text-text-mute hover:text-orange"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {pool.map((entry, i) => (
                      <ThumbCell
                        key={`pool-${entry}-${i}`}
                        url={entry}
                        checked={picked.has(entry)}
                        onToggle={() => toggle(entry)}
                      />
                    ))}
                  </div>
                </>
              )}

              {offPool.length > 0 && (
                <>
                  <div className="mt-5 mb-3 text-[11px] uppercase tracking-wide text-text-mute font-semibold flex items-center gap-2">
                    <span>Off-pool ({offPool.length})</span>
                    <span className="text-[10px] normal-case text-text-mute font-normal opacity-80">
                      — already attached to this part, not in the quote pool. Uncheck to drop.
                    </span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {offPool.map((entry, i) => (
                      <ThumbCell
                        key={`off-${entry}-${i}`}
                        url={entry}
                        checked={picked.has(entry)}
                        onToggle={() => toggle(entry)}
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>

        {/* ─── Footer ─── */}
        <div className="px-5 py-3 border-t border-line flex items-center justify-end gap-2 bg-bg/50 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm rounded border border-line-dark text-text hover:bg-bg"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-1.5 bg-orange text-white text-sm rounded font-semibold hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface ThumbCellProps {
  url: string;
  checked: boolean;
  onToggle: () => void;
}

function ThumbCell({ url, checked, onToggle }: ThumbCellProps) {
  const label = shortName(url);
  const cellClass = [
    'relative border rounded p-1 cursor-pointer transition-colors',
    checked ? 'border-orange bg-orange/5' : 'border-line-dark hover:border-orange/60',
  ].join(' ');

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onToggle}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggle();
        }
      }}
      className={cellClass}
      title={label}
    >
      {/* Checkbox overlay */}
      <div
        className={[
          'absolute top-1 right-1 z-10 w-5 h-5 rounded flex items-center justify-center text-[12px] font-bold border',
          checked
            ? 'bg-orange text-white border-orange'
            : 'bg-white text-transparent border-line-dark',
        ].join(' ')}
        aria-hidden
      >
        ✓
      </div>

      <div className="flex justify-center pointer-events-none">
        {isPdfUrl(url) ? (
          <PdfThumbnail source={url} width={140} />
        ) : isImageUrl(url) ? (
          <div
            className="bg-white flex items-center justify-center overflow-hidden"
            style={{ width: 140, height: 140 }}
          >
            <img
              src={url}
              alt={label}
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
              loading="lazy"
            />
          </div>
        ) : (
          <div
            className="flex flex-col items-center justify-center text-center bg-bg/40"
            style={{ width: 140, minHeight: 140 * (11 / 8.5) }}
          >
            <div className="text-3xl mb-1">📄</div>
            <div className="text-[10px] text-text-mute break-all px-1 line-clamp-2">
              {label}
            </div>
          </div>
        )}
      </div>
      <div
        className="mt-1 text-[10px] text-text-mute truncate text-center"
        style={{ maxWidth: 140 }}
      >
        {label}
      </div>
    </div>
  );
}
